import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';
import { LoadingOptions } from '../../../../models/shared/loading-options';
import { MemberUser } from '../../../../models/users/dto/member-user';
import { UpdateContactInfoRequest } from '../../../../models/users/requests/submissionRequests/update-contact-info-request';
import { MediaUtils } from '../../../../utils/media-utils';
import { PhoneNumberUtils } from '../../../../utils/phone-number-utils';
import { UpdateContactInfoSubmissionFormViewModel } from './update-contact-info-submission-form-view-model';
import { maxFileSizeValidator } from '../../../../ui/form-components/file-uploader/file-upload-field/max-file-size-validator';

type UpdateContactInfoForm = FormGroup<{
  primaryPhoneNumber: FormControl<string | null>;
  primaryPhoneNumberCountryCode: FormControl<string | null>;
  secondaryPhoneNumber: FormControl<string | null>;
  secondaryPhoneNumberCountryCode: FormControl<string | null>;
  files: FormControl<File[]>;
}>;

@Component({
  selector: 'app-update-contact-info-submission-form',
  templateUrl: './update-contact-info-submission-form.component.html',
  styleUrls: ['./update-contact-info-submission-form.component.scss'],
  providers: [UpdateContactInfoSubmissionFormViewModel],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpdateContactInfoSubmissionFormComponent implements OnChanges {
  @Input() user!: MemberUser;
  @Input() acceptedFileTypes: string[] = ['image/png', 'image/jpeg', 'application/pdf'];
  @Input() allowMultipleFiles: boolean = false;
  @Input() maxFileSizeInMb: number = 3;
  @Input() filesRequired: boolean = false;

  @Output() loadingOpts = new EventEmitter<LoadingOptions>();

  private viewModel = inject(UpdateContactInfoSubmissionFormViewModel);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  public formGroup: UpdateContactInfoForm = this.buildForm();
  public formSubmitted = false;

  public readonly countryCodes$ = this.viewModel.countryCodes$;

  public readonly customErrorMessages = {
    phoneNumbers: {
      atLeastOnePhoneNumber: $localize`Must enter at least one phone number`
    },
    files: {
      required: $localize`A file upload is required`,
      maxFileSize: $localize`File size exceeds the maximum limit of ${this.maxFileSizeInMb}MB`
    }
  };

  constructor() {
    this.viewModel.loadingOpts$.pipe(takeUntilDestroyed()).subscribe(opts => this.loadingOpts.emit(opts));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.user && this.user) {
      this.resetFormFromUser();
      this.cdr.markForCheck();
    }
    if (changes.filesRequired || changes.maxFileSizeInMb) {
      this.applyFileValidators();
    }
  }

  public submitForm(): void {
    this.formSubmitted = true;
    this.formGroup.markAllAsTouched();
    this.cdr.markForCheck();
    if (this.formGroup.invalid) return;

    const request = this.buildRequest();
    const files = this.formGroup.controls.files.value;
    if (this.filesRequired && files.length) {
      Promise.all(files.map(file => MediaUtils.fileToUserFile(file))).then(userFiles => {
        this.viewModel.submitRequest(request, userFiles);
      });
    } else {
      this.viewModel.submitRequest(request);
    }
  }

  private buildForm(): UpdateContactInfoForm {
    return new FormGroup(
      {
        primaryPhoneNumber: new FormControl<string | null>(null),
        primaryPhoneNumberCountryCode: new FormControl<string | null>('CA'),
        secondaryPhoneNumber: new FormControl<string | null>(null),
        secondaryPhoneNumberCountryCode: new FormControl<string | null>('CA'),
        files: new FormControl<File[]>({ value: [], disabled: true }, { nonNullable: true })
      },
      { validators: atLeastOnePhoneNumberValidator }
    );
  }

  private resetFormFromUser(): void {
    this.countryCodes$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(countryCodes => {
      const primaryCountryCode = this.user.primaryPhoneNumberCountryCode ?? 'CA';
      const secondaryCountryCode = this.user.secondaryPhoneNumberCountryCode ?? 'CA';
      const primaryNumber = this.user.primaryPhoneNumber
        ? PhoneNumberUtils.removeCodePrefix(this.user.primaryPhoneNumber, primaryCountryCode, countryCodes)
        : null;
      const secondaryNumber = this.user.secondaryPhoneNumber
        ? PhoneNumberUtils.removeCodePrefix(this.user.secondaryPhoneNumber, secondaryCountryCode, countryCodes)
        : null;

      this.formGroup.reset(
        {
          primaryPhoneNumber: primaryNumber,
          primaryPhoneNumberCountryCode: primaryCountryCode,
          secondaryPhoneNumber: secondaryNumber,
          secondaryPhoneNumberCountryCode: secondaryCountryCode,
          files: []
        },
        { emitEvent: false }
      );
      this.applyFileValidators();
      this.formSubmitted = false;
      this.formGroup.updateValueAndValidity();
      this.cdr.markForCheck();
    });
  }

  private applyFileValidators(): void {
    const filesControl = this.formGroup.controls.files;
    if (this.filesRequired) {
      filesControl.setValidators([Validators.required, maxFileSizeValidator(this.maxFileSizeInMb)]);
      filesControl.enable({ emitEvent: false });
    } else {
      filesControl.clearValidators();
      filesControl.setValue([], { emitEvent: false });
      filesControl.disable({ emitEvent: false });
    }
    filesControl.updateValueAndValidity({ emitEvent: false });
  }

  private buildRequest(): UpdateContactInfoRequest {
    const value = this.formGroup.getRawValue();
    const request = new UpdateContactInfoRequest(this.user);
    request.primaryPhoneNumber = value.primaryPhoneNumber ?? '';
    request.secondaryPhoneNumber = value.secondaryPhoneNumber ?? '';
    if (value.primaryPhoneNumber) {
      request.primaryPhoneNumberCountryCode = value.primaryPhoneNumberCountryCode ?? undefined;
    } else {
      delete request.primaryPhoneNumberCountryCode;
    }
    if (value.secondaryPhoneNumber) {
      request.secondaryPhoneNumberCountryCode = value.secondaryPhoneNumberCountryCode ?? undefined;
    } else {
      delete request.secondaryPhoneNumberCountryCode;
    }
    return request;
  }
}

function atLeastOnePhoneNumberValidator(group: AbstractControl): ValidationErrors | null {
  const primary = group.get('primaryPhoneNumber')?.value;
  const secondary = group.get('secondaryPhoneNumber')?.value;
  return primary || secondary ? null : { atLeastOnePhoneNumber: true };
}
